import React from "react";
import { useSearchParams } from "react-router-dom";
import { TopBar } from "@/components/TopBar";
import LembarKerjaJersey, { TombolUnduhLembarKerja } from "@/components/LembarKerjaJersey";
import { apiListSpks, apiGetSpk, apiSetSpkReady, apiUbahRencana, apiAjukanBatalSpk, formatApiErrorDetail } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/roles";
import { Loader2, RefreshCw, Eye, X, ArrowRight, CheckCircle2 } from "lucide-react";
import { useCetak } from "@/hooks/usePengaturan";
import { spkTunggu, rencanaTeks, qtyTeks } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import * as W from "@/lib/waktu";
import { namaLangkah } from "@/lib/posNama";
import usePetaPos from "@/lib/usePetaPos";
import FileMaster from "@/components/FileMaster";
import { bolehLihatFileMaster } from "@/lib/fileMaster";

const rupiah = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
const STATUS_STYLE = {
  DRAFT: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  READY: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  CANCELLED: "border-red-500/30 bg-red-500/10 text-red-300",
};

// F1.5-G / Addendum 2: planned finish (date, or date + hour) vs actual. The
// owner may move it with a reason; the cashier may fill an empty plan
// (Custom); a Jersey plan is computed at the customer's ACC.
function RencanaSelesai({ detail, isOwner, isKasir, onChanged }) {
  const [open, setOpen] = React.useState(false);
  const [f, setF] = React.useState({ tanggal: detail.rencana_selesai || "", jam: detail.rencana_selesai_jam || "", alasan: "" });
  const [error, setError] = React.useState(null);
  const simpan = async () => {
    setError(null);
    try { const r = await apiUbahRencana(detail.order_id, f.tanggal, f.alasan, f.jam || null); onChanged(r); setOpen(false); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const boleh = detail.rencana_status !== "MENUNGGU_ACC" && (isOwner || (isKasir && !detail.rencana_selesai));
  return (
    <span data-testid="spk-rencana">Rencana selesai: <b>{rencanaTeks(detail)}</b>{detail.layanan === "EXPRESS" ? " (Express)" : ""}
      {detail.selesai_aktual ? <> · selesai aktual {W.waktu(detail.selesai_aktual)}</> : null}
      {boleh && !open && <button data-testid="spk-rencana-ubah" onClick={() => setOpen(true)} className="ml-2 text-xs text-amber-300 hover:underline">{detail.rencana_selesai ? "ubah" : "isi"}</button>}
      {open && <span className="inline-flex flex-wrap items-center gap-2 ml-2">
        <input type="date" value={f.tanggal} onChange={(e) => setF({ ...f, tanggal: e.target.value })} className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs" />
        <input type="time" value={f.jam} onChange={(e) => setF({ ...f, jam: e.target.value })} className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs" />
        <input placeholder="alasan (wajib)" value={f.alasan} onChange={(e) => setF({ ...f, alasan: e.target.value })} className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs" />
        <button onClick={simpan} className="text-xs text-emerald-300">simpan</button><button onClick={() => setOpen(false)} className="text-xs text-slate-400">batal</button>
        {error && <span className="text-xs text-red-300">{error}</span>}</span>}
    </span>
  );
}

// owner 2026-10-12: the Manager asks the owner to cancel an SPK (reason required);
// the owner decides and handles the nota / money (menu Pengajuan Batal SPK).
function AjukanBatal({ spk }) {
  const [buka, setBuka] = React.useState(false);
  const [alasan, setAlasan] = React.useState("");
  const [pesan, setPesan] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const kirim = async () => {
    setBusy(true); setPesan(null);
    try { await apiAjukanBatalSpk(spk.id, alasan.trim()); setPesan({ ok: true, t: "Pengajuan terkirim ke Owner." }); setBuka(false); }
    catch (e) { setPesan({ ok: false, t: formatApiErrorDetail(e.response?.data?.detail) || e.message }); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="spk-ajukan-batal" className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 space-y-2">
      {!buka ? (
        <button data-testid="spk-ajukan-batal-buka" onClick={() => setBuka(true)} className="rounded-md border border-red-400/60 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50">Ajukan batal SPK ke Owner</button>
      ) : (<>
        <label className="block text-[13px] font-semibold text-slate-200">Alasan pembatalan (wajib, min. 10 huruf)</label>
        <textarea data-testid="spk-ajukan-batal-alasan" value={alasan} onChange={(e) => setAlasan(e.target.value)} rows={2}
          className="w-full rounded-md border border-slate-700 bg-slate-950 px-2.5 py-2 text-sm text-slate-100" placeholder="mis. customer membatalkan pesanan, file salah" />
        <div className="flex gap-2">
          <button data-testid="spk-ajukan-batal-kirim" disabled={busy || alasan.trim().length < 10} onClick={kirim} className="rounded-md bg-red-600 px-3 py-1.5 text-sm text-white disabled:opacity-40">Kirim pengajuan</button>
          <button onClick={() => setBuka(false)} className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300">Batal</button>
        </div>
        <p className="text-xs text-slate-500">Owner yang memutuskan dan mengurus nota/uang. SPK tidak berubah sampai Owner bertindak.</p>
      </>)}
      {pesan && <p data-testid="spk-ajukan-batal-pesan" className={`text-sm ${pesan.ok ? "text-emerald-600" : "text-red-600"}`}>{pesan.t}</p>}
    </div>
  );
}

// owner 2026-10-15 (A1): sort the list
// v2.12: an operator's list is grouped in this order
const POS_SAYA = [["SEDANG", "Sedang dikerjakan"], ["AKAN_DATANG", "Akan datang"], ["SELESAI", "Selesai beberapa hari terakhir"]];
const posSayaLabel = (r) => (POS_SAYA.find(([k]) => k === r.pos_saya?.status) || [])[1];
const URUT = { baru: "Terbaru", lama: "Terlama", kasir: "Kasir/CS (A–Z)", spk: "No. SPK" };
function urutkan(rows, urut) {
  const r = [...rows];
  if (urut === "lama") r.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  else if (urut === "kasir") r.sort((a, b) => (a.cs_nama || "~").localeCompare(b.cs_nama || "~") || (b.created_at || "").localeCompare(a.created_at || ""));
  else if (urut === "spk") r.sort((a, b) => (a.nomor_spk || "").localeCompare(b.nomor_spk || ""));
  return r;  // "baru": the server's order (newest first)
}

const H1 = "rounded bg-amber-500/20 px-1.5 py-0.5 text-[11px] font-semibold text-amber-300";  // v2.18 D: next-day ACC revision

export default function SpkManagement() {
  const { user } = useAuth();
  const petaPos = usePetaPos();  // v2.17.2: station names, not codes
  const [sp, setSp] = useSearchParams();
  const isOwner = user?.role === "admin";
  const isManager = user?.role === "manager";  // owner 2026-10-12: no money; requests a cancellation
  // owner 2026-10-15 (A6): every role reads SPKs; money only for Owner, Kasir/CS, Finance (the server strips it too)
  const uang = ["admin", "kasir", "finance"].includes(user?.role);
  const kantor = ["admin", "kasir"].includes(user?.role);  // Set READY, print: Owner / Kasir/CS (server)
  // v2.12 (owner 2026-10-22, requirement 75): an operator gets only "SPK untuk pos saya" from the
  // server (pos_saya = SEDANG / AKAN_DATANG / SELESAI); the office roles read every SPK
  const operator = !["admin", "manager", "kasir", "finance", "desain"].includes(user?.role);
  const [kasir, setKasir] = React.useState("");
  const [urut, setUrut] = React.useState("baru");
  const [cari, setCari] = React.useState(() => sp.get("q") || "");  // v2.11: the label QR opens /spk?q=<nomor SPK>
  const cetak = useCetak();
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [notice, setNotice] = React.useState(null);
  const [detail, setDetail] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await apiListSpks());
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);
  // owner 2026-10-15: the queue's "Lembar kerja" opens an SPK here (/spk?id=...)
  const bukaId = sp.get("id");
  React.useEffect(() => {
    if (bukaId) apiGetSpk(bukaId).then(setDetail).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [bukaId]);

  const daftarKasir = React.useMemo(() => {
    const m = new Map();
    rows.forEach((r) => r.cs_id && m.set(r.cs_id, r.cs_nama || "-"));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);
  const needle = cari.trim().toLowerCase();
  const disaring = rows.filter((r) => (!kasir || r.cs_id === kasir) && (!needle || [r.nomor_spk, r.nomor_faktur, r.nomor_order,
    r.customer_snapshot?.nama].some((v) => (v || "").toLowerCase().includes(needle))));
  const tampil = operator
    ? POS_SAYA.flatMap(([k]) => urutkan(disaring.filter((r) => r.pos_saya?.status === k), urut))
    : urutkan(disaring, urut);
  const jumlahPosSaya = Object.fromEntries(POS_SAYA.map(([k]) => [k, disaring.filter((r) => r.pos_saya?.status === k).length]));

  const flash = (m) => { setNotice(m); setTimeout(() => setNotice(null), 2500); };

  const setReady = async (spk) => {
    setError(null);
    try {
      const updated = await apiSetSpkReady(spk.id);
      flash(`SPK ${spk.nomor_spk} → READY`);
      await load();
      if (detail?.id === spk.id) setDetail(updated);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="spk-management-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Manajemen SPK</h1>
          <p className="text-sm text-slate-400 mt-1">Surat Perintah Kerja — snapshot order & workflow saat SPK dibuat. Status tiap step ada di detail SPK.
            {operator && " Hanya SPK untuk pos Anda: sedang dikerjakan, akan datang, dan yang selesai beberapa hari terakhir. Pekerjaan tetap di menu Antrean."}</p>
        </div>

        {notice && <div data-testid="spk-notice" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300">{notice}</div>}
        {error && <div data-testid="spk-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}

        {detail && (
          <div data-testid="spk-detail" className="rounded-xl border border-amber-500/30 bg-slate-900/60 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-100">{detail.nomor_spk}</h2>
              <button data-testid="spk-detail-close" onClick={() => { setDetail(null); if (bukaId) setSp({}); }} className="text-slate-400 hover:text-slate-200"><X size={18} /></button>
            </div>
            <div className="text-sm text-slate-400 flex flex-wrap gap-x-4 gap-y-1">
              <span>Order: <span className="font-mono text-amber-300">{detail.nomor_order}</span></span>
              {detail.nomor_faktur && <span>Faktur: <span data-testid="spk-detail-faktur" className="font-mono text-amber-300">{detail.nomor_faktur}</span></span>}
              {detail.jenis_mesin_nama && <span>Mesin: <span className="text-slate-200">{detail.jenis_mesin_nama}</span></span>}
              {detail.designer && <span>Designer: <span className="text-slate-200">{detail.designer.nama}</span></span>}
              <span>Customer: <span className="text-slate-200">{detail.customer_snapshot?.nama}</span></span>
              <span>Kasir/CS: <span data-testid="spk-detail-cs" className="text-slate-200">{detail.cs_nama || "-"}</span></span>
              <span>Status: <span className={`text-xs font-mono px-2 py-0.5 rounded border ${STATUS_STYLE[detail.status_spk]}`}>{detail.status_spk}</span></span>
              {detail.revisi_h1 && <span data-testid="spk-detail-h1" className={H1}>revisi H+1</span>}
              {detail.layanan && <RencanaSelesai detail={detail} isOwner={isOwner} isKasir={user?.role === "kasir"} onChanged={(r) => setDetail({ ...detail, rencana_selesai: r.rencana_selesai, rencana_selesai_jam: r.rencana_selesai_jam, rencana_status: null })} />}
            </div>

            {/* owner 2026-10-12: a Jersey SPK opens as a worksheet (+ Excel A4) */}
            {detail.jenis_transaksi === "JERSEY" && detail.jersey_snapshot ? <LembarKerjaJersey spkId={detail.id} /> : (
            <div>
              <h3 className="text-[13px] font-semibold text-slate-500 mb-2">Item Snapshot</h3>
              <table className="w-full text-sm">
                <tbody>
                  {detail.items_snapshot.map((it, i) => (
                    <tr key={i} className="border-b border-slate-800/60">
                      <td className="py-2 text-slate-300">{it.kategori_produk}/{it.jenis_pekerjaan}{it.produk ? ` · ${it.produk}` : ""}</td>
                      <td className="py-2 text-right font-mono">{qtyTeks(it)}</td>
                      {!detail.jenis_transaksi && uang && <td className="py-2 text-right font-mono">{rupiah(it.harga)}</td>}
                      {!detail.jenis_transaksi && uang && <td className="py-2 text-right font-mono text-slate-200">{rupiah(it.subtotal)}</td>}
                      {detail.jenis_transaksi && <td className="py-2 text-slate-400">{it.panjang_nyata_cm ? `panjang ${(it.panjang_nyata_cm / 100).toLocaleString("id-ID")} m · ` : it.p_cm ? `${it.p_cm / 100} × ${it.l_cm / 100} m · ` : ""}{it.finishing || ""}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
              {!detail.jenis_transaksi && uang && <div className="flex justify-end gap-6 text-sm font-mono mt-2">
                <span className="text-slate-400">Subtotal: <span className="text-slate-200">{rupiah(detail.subtotal_snapshot)}</span></span>
                <span className="text-slate-400">Diskon: <span className="text-slate-200">{rupiah(detail.diskon_snapshot)}</span></span>
                <span className="text-amber-300 font-semibold" data-testid="spk-detail-total">Total: {rupiah(detail.total_snapshot)}</span>
              </div>}
              {detail.jenis_transaksi && <p className="text-xs text-slate-500 mt-2">SPK produksi tidak memuat harga. Rincian harga ada di nota {detail.nomor_faktur}.</p>}
            </div>
            )}
            {/* v2.22: design master files (server decides who sees what; operators only final versions) */}
            {bolehLihatFileMaster(user?.role) && <FileMaster key={detail.id} spkId={detail.id} testid="spk-fm" />}
            {isManager && detail.status_spk !== "CANCELLED" && <AjukanBatal spk={detail} />}

            <div>
              <h3 className="text-[13px] font-semibold text-slate-500 mb-2">Workflow Instance</h3>
              <div className="space-y-4">
                {detail.workflow_instances.map((wi) => (
                  <div key={wi.instance_id} data-testid={`spk-wi-${wi.kategori}`} className="rounded-lg border border-slate-800 bg-slate-950/40 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="font-mono text-amber-300 text-sm">{wi.workflow_code}</span>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-cyan-500/30 bg-cyan-500/10 text-cyan-300">{wi.kategori}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {wi.steps.map((s, idx) => (
                        <React.Fragment key={idx}>
                          <span className="inline-flex flex-col items-center rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-1.5">
                            <span data-testid={`spk-detail-pos-${s.urutan}`} className="text-xs font-semibold text-slate-200">{namaLangkah(s, petaPos)}</span>
                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-600 bg-slate-800/40 text-slate-400 mt-1">{s.status}</span>
                          </span>
                          {idx < wi.steps.length - 1 && <ArrowRight size={14} className="text-slate-600" />}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {detail.status_spk === "DRAFT" && kantor && (  // owner 2026-10-12: Set READY = Owner / Kasir/CS (server)
              <button data-testid="spk-set-ready" onClick={() => setReady(detail)} className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400">
                <CheckCircle2 size={16} /> Set READY
              </button>
            )}
          </div>
        )}

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
          <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-slate-800">
            <h2 className="mr-auto text-[15px] font-bold text-soq-biru-tua">Daftar SPK <span className="text-xs font-normal text-slate-500">({tampil.length})</span></h2>
            <input data-testid="spk-cari" value={cari} onChange={(e) => setCari(e.target.value)} placeholder="Cari SPK / faktur / customer"
              className="w-full sm:w-56 rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm" />
            {!operator && <select data-testid="spk-filter-kasir" value={kasir} onChange={(e) => setKasir(e.target.value)} className="rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm">
              <option value="">Semua Kasir/CS</option>{daftarKasir.map(([id, nama]) => <option key={id} value={id}>{nama}</option>)}
            </select>}
            {operator && <span data-testid="spk-pos-saya" className="text-xs text-slate-400">{POS_SAYA.map(([k, l]) => `${l}: ${jumlahPosSaya[k]}`).join(" · ")}</span>}
            <select data-testid="spk-urut" value={urut} onChange={(e) => setUrut(e.target.value)} className="rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm">
              {Object.entries(URUT).map(([k, l]) => <option key={k} value={k}>Urut: {l}</option>)}
            </select>
            <button data-testid="spk-refresh" onClick={load} className="inline-flex items-center gap-2 text-xs font-mono text-slate-400 hover:text-slate-200"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
          </div>
          {loading ? (
            <div className="p-8 flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Memuat…</div>
          ) : (
            <>
            {/* owner 2026-10-15 (A6): every station reads SPKs, mostly on a phone — cards there */}
            <div data-testid="spk-kartu" className="sm:hidden divide-y divide-slate-800">
              {tampil.map((s) => (
                <div key={s.id} className="flex items-start">
                <button data-testid={`spk-kartu-${s.nomor_spk}`} onClick={async () => { setDetail(await apiGetSpk(s.id)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="block min-w-0 flex-1 px-4 py-3 text-left hover:bg-slate-800/30">
                  <span className="flex items-center justify-between gap-2">
                    <span className="font-mono font-semibold text-amber-300">{s.nomor_spk}</span>
                    <span className={`text-xs font-mono px-2 py-0.5 rounded-md border ${STATUS_STYLE[s.status_spk]}`}>{s.status_spk}</span>
                  </span>
                  <span className="block font-mono text-xs text-slate-500">{s.nomor_faktur ? `${s.nomor_faktur} · ${s.jenis_mesin_nama}` : s.nomor_order}</span>
                  <span className="block text-[15px] text-slate-100">{s.customer_snapshot?.nama || "—"}</span>
                  <span className="block text-xs text-slate-400">CS: {s.cs_nama || "—"}{spkTunggu(s) ? ` · ${spkTunggu(s)}` : ""}</span>
                  {s.revisi_h1 && <span data-testid={`spk-kartu-h1-${s.nomor_spk}`} className={`mt-1 inline-block ${H1}`}>revisi H+1</span>}
                  {operator && s.pos_saya && <span data-testid={`spk-pos-saya-${s.nomor_spk}`} className="block text-xs text-cyan-300">{posSayaLabel(s)}</span>}
                </button>
                {s.jenis_transaksi === "JERSEY" && <span className="shrink-0 py-3 pr-3"><TombolUnduhLembarKerja spkId={s.id} ikon testid={`spk-kartu-unduh-${s.nomor_spk}`} /></span>}
                </div>
              ))}
              {tampil.length === 0 && <p className="px-4 py-8 text-center text-slate-500">{rows.length ? "Tidak ada SPK yang cocok." : "Belum ada SPK."}</p>}
            </div>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm" data-testid="spk-table">
                <thead>
                  <tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
                    <th className="px-4 py-3">No. SPK</th>
                    <th className="px-4 py-3">No. Order / Faktur</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Kasir/CS</th>
                    {uang && <th className="px-4 py-3 text-right">Total</th>}
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {tampil.map((s) => (
                    <tr key={s.id} data-testid={`spk-row-${s.nomor_spk}`} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                      <td className="px-4 py-3 font-mono text-amber-300">{s.nomor_spk}</td>
                      <td className="px-4 py-3 font-mono text-slate-300">{s.nomor_faktur ? `${s.nomor_faktur} · ${s.jenis_mesin_nama}` : s.nomor_order}</td>
                      <td className="px-4 py-3 text-slate-300">{s.customer_snapshot?.nama || "—"}</td>
                      <td data-testid={`spk-cs-${s.nomor_spk}`} className="px-4 py-3 text-xs text-slate-300">{s.cs_nama || "—"}</td>
                      {uang && <td className="px-4 py-3 text-right font-mono text-slate-200">{s.jenis_transaksi ? "—" : rupiah(s.total_snapshot)}</td>}
                      <td className="px-4 py-3"><span data-testid={`spk-status-${s.nomor_spk}`} className={`text-xs font-mono px-2 py-1 rounded-md border ${STATUS_STYLE[s.status_spk]}`}>{s.status_spk}</span>
                        {spkTunggu(s) && <span className="block mt-1 text-[11px] text-amber-300">{spkTunggu(s)}</span>}
                        {s.revisi_h1 && <span data-testid={`spk-h1-${s.nomor_spk}`} className={`mt-1 inline-block ${H1}`}>revisi H+1</span>}
                        {operator && s.pos_saya && <span data-testid={`spk-pos-saya-${s.nomor_spk}`} className="block mt-1 text-[11px] text-cyan-300">{posSayaLabel(s)}</span>}</td>
                      <td className="px-4 py-3 text-right">
                        {cetak.spk && s.jenis_transaksi && kantor && <a data-testid={`spk-print-${s.nomor_spk}`} href={`/spk/${s.id}/cetak`} target="_blank" rel="noopener noreferrer" className="mr-1 inline-flex items-center rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300">Cetak</a>}
                        {s.jenis_transaksi === "JERSEY" && <span className="mr-1 align-top"><TombolUnduhLembarKerja spkId={s.id} ikon testid={`spk-unduh-${s.nomor_spk}`} /></span>}
                        <button data-testid={`spk-view-${s.nomor_spk}`} onClick={async () => setDetail(await apiGetSpk(s.id))} className="inline-flex items-center rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-cyan-500/40 hover:text-cyan-300"><Eye size={13} /></button>
                      </td>
                    </tr>
                  ))}
                  {tampil.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">{rows.length ? "Tidak ada SPK yang cocok." : "Belum ada SPK."}</td></tr>}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
